import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiEye, FiEyeOff, FiCopy, FiCheck, FiRefreshCw,
  FiAlertTriangle, FiLock,
  FiGlobe, FiX, FiPlus, FiUser, FiCreditCard, FiUsers, FiTrash2,
} from 'react-icons/fi';
import InviteUserModal from '../components/InviteUserModal';
import { useAuth } from '../contexts/AuthContext';
// import IntegrationsModal from '../components/IntegrationsModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { useConfirm } from '../components/ConfirmDialog';
import CancelSubscriptionModal from '../components/CancelSubscriptionModal';
import PlanCard from '../components/plancard/PlanCard';
import AddressAutocomplete from '../components/AddressAutocomplete/AddressAutocomplete';
import { supabase } from '../utils/supabaseClient';
import { canCustomiseReport as canCustomiseReportFor } from '../utils/reportEntitlements';
import { COMMON_COUNTRIES, COUNTRIES, countryCodeOf, countryName } from '../utils/countries';
import './Billing.css';
import './Profile.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
// Where an AI assistant connects to Zilch: the API's origin + /mcp.
const MCP_URL = /^https?:\/\//.test(API_BASE_URL)
  ? API_BASE_URL.replace(/\/api\/?$/, '/mcp')
  : `${window.location.origin}/mcp`;

/**
 * The logo goes on PDFs (the policies Zilch drafts), which can only carry PNG
 * or JPEG, so anything else (WebP, GIF, SVG…) is redrawn as a PNG before it's
 * uploaded. If the browser can't draw it, the original goes up unchanged.
 */
async function logoForUpload(file) {
  if (/^image\/(png|jpe?g)$/i.test(file.type)) return { file, ext: file.type.toLowerCase() === 'image/png' ? 'png' : 'jpg' };
  const fallback = { file, ext: (file.name.split('.').pop() || 'png').toLowerCase() };
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    // An SVG may have no size of its own; draw it at a size that stays sharp.
    const w = img.naturalWidth || 1024;
    const h = img.naturalHeight || 512;
    const scale = Math.min(1, 1600 / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? { file: blob, ext: 'png' } : fallback;
  } catch {
    return fallback;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const formatStatusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'trialing') return 'Trial';
  if (s === 'past_due') return 'Past due';
  if (s === 'incomplete') return 'Incomplete';
  if (s === 'canceled') return 'Canceled';
  return 'Inactive';
};

const statusBadgeClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active' || s === 'trialing') return 'text-bg-success';
  if (s === 'past_due' || s === 'incomplete') return 'text-bg-warning';
  if (s === 'canceled') return 'text-bg-secondary';
  return 'text-bg-light';
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return String(iso);
  }
};

const clampPercent = (value) => Math.max(0, Math.min(100, value));

// Read the cached role synchronously so the admin-only Billing tab is in the
// sidebar on the very first paint, instead of popping in seconds later when
// /auth/me resolves. AuthContext is the source of truth and corrects this as
// soon as supplierInfo lands; the server still guards every billing route.
const readCachedRole = () => {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem('zilch_role') : null;
  } catch (_) {
    return null;
  }
};

const getWarehouseFromAddresses = (addresses) => {
  if (!Array.isArray(addresses)) return null;
  return addresses.find((a) => a && a.type === 'warehouse') || null;
};

const ALL_SETTINGS_TABS = [
  { id: 'profile', label: 'Profile', icon: FiUser },
  { id: 'team', label: 'Team', icon: FiUsers },
  { id: 'billing', label: 'Billing', icon: FiCreditCard, adminOnly: true },
];

export default function Profile() {
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [apiKeyRow, setApiKeyRow] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orgIdCopied, setOrgIdCopied] = useState(false);
  const [showConfirmRotate, setShowConfirmRotate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [, setNewlyGeneratedKey] = useState(null);
  // Secret (server-to-server) key — Partner API + Zapier. Tracked separately from the
  // publishable widget key above because the two rotate independently.
  const [secretKeyRow, setSecretKeyRow] = useState(null);
  const [secretKeyVisible, setSecretKeyVisible] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [showConfirmRotateSecret, setShowConfirmRotateSecret] = useState(false);
  const [generatingSecret, setGeneratingSecret] = useState(false);
  // const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  // const [selectedIntegrationCategory, setSelectedIntegrationCategory] = useState('Accounting');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [updatingDomains, setUpdatingDomains] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [cadence, setCadence] = useState('annual');
  // Pre-trial "remind me before billing" opt-in (off by default).
  const [reminderOptIn, setReminderOptIn] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ name: '', phone_1: '', website: '', country: '', account_type: '' });
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingCatalogueVisibility, setSavingCatalogueVisibility] = useState(false);
  const [savingResultsVisibility, setSavingResultsVisibility] = useState(false);
  // Both PDF report switches write the same suppliers row, so one save at a time.
  const [savingReportOption, setSavingReportOption] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [teamSeatLimit, setTeamSeatLimit] = useState(3);
  const [teamSeatUsed, setTeamSeatUsed] = useState(0);
  const [canInviteMembers, setCanInviteMembers] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, supplierInfo, signOut, changePassword, billing, refreshSupplierInfo, refreshBillingInfo } = useAuth();
  const { confirm } = useConfirm();
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    let timeoutId;

    (async () => {
      try {
        setProfileLoading(true);
        setError('');

        timeoutId = setTimeout(() => {
          console.warn('Profile loading timeout - forcing completion');
          setProfileLoading(false);
        }, 5000);

        const profilePromise = axios.get(`${API_BASE_URL}/suppliers/profile`)
          .then(r => r?.data?.data || null)
          .catch(err => { console.error('Failed to load profile:', err); return null; });

        // Returns the whole envelope: `data` is the publishable (widget) key and
        // `secret_key` is the server-to-server key used by the Partner API and Zapier.
        const keyPromise = axios.get(`${API_BASE_URL}/partner/api-key`)
          .then(r => r?.data || null)
          .catch(err => { console.error('Failed to load API key:', err); return null; });

        const [profileData, keyData] = await Promise.all([profilePromise, keyPromise]);

        setProfile(profileData);
        setApiKeyRow(keyData?.data || null);
        setSecretKeyRow(keyData?.secret_key || null);

        if (keyData?.data?.api_key) {
          try {
            const configResponse = await axios.get(`${API_BASE_URL}/partner/widget-config`, {
              headers: { 'x-api-key': keyData.data.api_key }
            });
            setAllowedDomains(configResponse.data?.data?.allowed_domains || []);
          } catch (err) {
            console.error('Failed to load allowed domains:', err);
            setAllowedDomains([]);
          }
        }

        clearTimeout(timeoutId);
      } catch (e) {
        console.error('Error loading profile page:', e);
        setError('Failed to load profile');
      } finally {
        setProfileLoading(false);
      }
    })();

    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [user, navigate]);

  const handleEditAccount = () => {
    const warehouse = getWarehouseFromAddresses(profile?.addresses);
    setAccountForm({
      name: profile?.name || '',
      phone_1: profile?.phone_1 || '',
      website: profile?.website || '',
      // Older records hold "Australia" or "AU"; the picker works in codes.
      country: countryCodeOf(profile?.country),
      account_type: profile?.account_type || '',
      warehouse_address: warehouse?.raw || '',
      warehouse_lat: warehouse?.lat ?? null,
      warehouse_lng: warehouse?.lng ?? null,
    });
    setEditingAccount(true);
  };

  const handleSaveAccount = async () => {
    try {
      setSavingAccount(true);
      setError('');

      const {
        warehouse_address: warehouseAddress,
        warehouse_lat: warehouseLat,
        warehouse_lng: warehouseLng,
        ...rest
      } = accountForm;
      const payload = { ...rest };

      // Preserve any non-warehouse entries (e.g. imported promo addresses);
      // replace just the warehouse entry with the edited value (or drop it).
      const existing = Array.isArray(profile?.addresses) ? profile.addresses : [];
      const nonWarehouse = existing.filter((a) => a && a.type !== 'warehouse');
      const trimmed = (warehouseAddress || '').trim();
      if (trimmed) {
        const entry = { type: 'warehouse', raw: trimmed };
        if (warehouseLat != null && warehouseLng != null) {
          entry.lat = warehouseLat;
          entry.lng = warehouseLng;
        }
        payload.addresses = [...nonWarehouse, entry];
      } else {
        payload.addresses = nonWarehouse;
      }

      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile`, payload);
      if (data?.success) {
        setProfile(data.data);
        setEditingAccount(false);
        setSuccess('Profile updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !supplierInfo?.supplier_id) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5MB');
      e.target.value = '';
      return;
    }
    setUploadingLogo(true);
    setError('');
    try {
      const { file: upload, ext } = await logoForUpload(file);
      const filePath = `${supplierInfo.supplier_id}/org-logo-${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('product-images')
        .upload(filePath, upload, { cacheControl: '3600', upsert: true, contentType: upload.type || undefined });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(filePath);
      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile`, { logo_url: urlData.publicUrl });
      if (data?.success) {
        setProfile(data.data);
        await refreshSupplierInfo();
        setSuccess('Logo updated');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      setError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setError('');
    try {
      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile`, { logo_url: null });
      if (data?.success) {
        setProfile(data.data);
        await refreshSupplierInfo();
        setSuccess('Logo removed');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to remove logo');
    }
  };

  const handleCatalogueVisibilityToggle = async (next) => {
    try {
      setSavingCatalogueVisibility(true);
      setError('');
      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile/catalogue-visibility`, {
        is_catalogue_public: next,
      });
      if (data?.success) {
        setProfile(p => p ? { ...p, is_catalogue_public: data.data.is_catalogue_public } : p);
        setSuccess(next ? 'Catalogue is now public to distributors' : 'Catalogue is now private');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update catalogue visibility');
    } finally {
      setSavingCatalogueVisibility(false);
    }
  };

  const handleResultsVisibilityToggle = async (next) => {
    try {
      setSavingResultsVisibility(true);
      setError('');
      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile/results-visibility`, {
        is_results_public: next,
      });
      if (data?.success) {
        setProfile(p => p ? { ...p, is_results_public: data.data.is_results_public } : p);
        setSuccess(next
          ? 'Results pages are public again — existing links work as before'
          : 'Results pages are now private — shared links will no longer open, and distributors no longer see your products');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update results visibility');
    } finally {
      setSavingResultsVisibility(false);
    }
  };

  // `field` is the column name ('pdf_include_methodology' | 'pdf_show_zilch_branding'
  // | 'pdf_show_full_date').
  const handleReportOptionToggle = async (field, next) => {
    try {
      setSavingReportOption(true);
      setError('');
      const { data } = await axios.patch(`${API_BASE_URL}/suppliers/profile/report-options`, {
        [field]: next,
      });
      if (data?.success) {
        setProfile(p => (p ? { ...p, ...data.data } : p));
        setSuccess('Report settings saved — new PDF downloads use them straight away');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update report settings');
    } finally {
      setSavingReportOption(false);
    }
  };

  const handleGenerateOrRotate = async () => {
    try {
      setGenerating(true);
      setError('');
      const { data } = await axios.post(`${API_BASE_URL}/partner/api-key/generate`);
      if (data?.success && data?.data) {
        setNewlyGeneratedKey(data.data.api_key);
        setApiKeyRow(data.data);
        setShowConfirmRotate(false);
        setSuccess('API key generated successfully');
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateOrRotateSecret = async () => {
    try {
      setGeneratingSecret(true);
      setError('');
      const { data } = await axios.post(`${API_BASE_URL}/partner/api-key/generate`, {
        key_type: 'secret',
      });
      if (data?.success && data?.data) {
        setSecretKeyRow(data.data);
        setSecretKeyVisible(true);
        setShowConfirmRotateSecret(false);
        setSuccess('Secret API key generated. Copy it now and store it somewhere safe.');
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to generate secret API key');
    } finally {
      setGeneratingSecret(false);
    }
  };

  const handleChangePassword = async (newPassword) => {
    try {
      await changePassword(newPassword);
      setSuccess('Password changed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      throw err;
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  const isOverride = useMemo(() => !!billing?.is_override, [billing?.is_override]);
  const plan = useMemo(() => billing?.plan || 'free', [billing?.plan]);
  const status = useMemo(() => billing?.status || 'inactive', [billing?.status]);
  // Widget embedding + partner API require an active or trialing subscription —
  // only free suppliers cannot generate or use a key. Mirrors the server-side gate.
  const hasWidgetAccess = useMemo(() => isOverride || status === 'active' || status === 'trialing', [isOverride, status]);
  // The secret (server-to-server) key is the Partner API — Team and above only.
  const canUsePartnerApi = useMemo(() => isOverride || !!billing?.can_use_partner_api, [isOverride, billing?.can_use_partner_api]);
  // The Zilch MCP server authenticates with the secret key too, and Starter has
  // it for a limited time (mcp_limited_time). An API older than this client
  // sends neither flag, which falls back to the Partner API rule above.
  const canUseMcp = useMemo(() => isOverride || !!billing?.can_use_mcp, [isOverride, billing?.can_use_mcp]);
  const canGenerateSecretKey = canUsePartnerApi || canUseMcp;
  const mcpLimitedTime = !isOverride && !!billing?.mcp_limited_time;
  const canManage = useMemo(() => !!billing?.stripe_portal_available, [billing?.stripe_portal_available]);
  const skuUsed = useMemo(() => billing?.sku_used ?? null, [billing?.sku_used]);
  const skuLimit = useMemo(() => billing?.sku_limit ?? null, [billing?.sku_limit]);
  const assessmentUsed = useMemo(() => billing?.assessment_used ?? billing?.assessed_sku_used ?? null, [billing?.assessment_used, billing?.assessed_sku_used]);
  const assessmentLimit = useMemo(() => billing?.assessment_limit ?? null, [billing?.assessment_limit]);
  const domainLimit = useMemo(() => billing?.domain_limit ?? null, [billing?.domain_limit]);
  const canDownloadPdf = useMemo(() => !!billing?.can_download_pdf, [billing?.can_download_pdf]);
  const canViewMethodology = useMemo(() => !!billing?.can_view_methodology, [billing?.can_view_methodology]);
  // Trimming the PDF (no methodology pages, no Zilch mark) is Professional+.
  // See utils/reportEntitlements for why a missing flag is not a denial.
  const canCustomiseReport = useMemo(
    () => canCustomiseReportFor(billing, isOverride),
    [billing, isOverride],
  );
  const periodEnd = useMemo(() => billing?.current_period_end || null, [billing?.current_period_end]);
  // Only so the report-date examples below read as a real date rather than a
  // year the reader has to translate.
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const skuPercent = useMemo(() => {
    if (skuUsed == null || skuLimit == null || skuLimit === 0) return 0;
    return clampPercent((skuUsed / skuLimit) * 100);
  }, [skuUsed, skuLimit]);

  const assessmentPercent = useMemo(() => {
    if (assessmentUsed == null || assessmentLimit == null || assessmentLimit === 0) return 0;
    return clampPercent((assessmentUsed / assessmentLimit) * 100);
  }, [assessmentUsed, assessmentLimit]);

  const currentPlanDetails = {
    statusLabel: formatStatusLabel(status),
    statusClass: statusBadgeClass(status),
    periodEnd: periodEnd ? formatDate(periodEnd) : null,
    skuUsed,
    skuLimit,
    skuPercent,
    assessmentUsed,
    assessmentLimit,
    assessmentPercent,
    domainLimit,
    canDownloadPdf,
    canViewMethodology,
  };

  const banner = useMemo(() => {
    const p = location?.pathname || '';
    if (p.endsWith('/success')) return { variant: 'success', title: 'Subscription started', body: "Thanks — your plan will update once Stripe confirms payment. If it doesn't update in a minute, hit Refresh." };
    if (p.endsWith('/cancel')) return { variant: 'secondary', title: 'Checkout canceled', body: 'No changes were made. You can try again any time.' };
    return null;
  }, [location?.pathname]);

  const startCheckout = async ({ tier, cadence, trial = false, reminder_opt_in = false }) => {
    setBillingLoading(true);
    setBillingError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/billing/checkout-session`, { tier, cadence, trial, reminder_opt_in });
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e) {
      setBillingError(e.response?.data?.error || e.message || 'Failed to start checkout');
    } finally {
      setBillingLoading(false);
    }
  };

  const endTrial = async () => {
    setBillingLoading(true);
    setBillingError('');
    try {
      await axios.post(`${API_BASE_URL}/billing/end-trial`, {});
      if (refreshBillingInfo) await refreshBillingInfo();
    } catch (e) {
      setBillingError(e.response?.data?.error || e.message || 'Failed to end trial');
    } finally {
      setBillingLoading(false);
    }
  };

  const toggleTrialReminder = async (optIn) => {
    try {
      await axios.post(`${API_BASE_URL}/billing/trial-reminder`, { opt_in: optIn });
      if (refreshBillingInfo) await refreshBillingInfo();
    } catch (e) {
      setBillingError(e.response?.data?.error || e.message || 'Failed to update reminder preference');
    }
  };

  const openPortal = async () => {
    setBillingLoading(true);
    setBillingError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/billing/portal-session`, {});
      if (!data?.url) throw new Error('No portal URL returned');
      window.location.href = data.url;
    } catch (e) {
      setBillingError(e.response?.data?.error || e.message || 'Failed to open billing portal');
    } finally {
      setBillingLoading(false);
    }
  };

  const starterMonthlyPrice = 109;
  const teamMonthlyPrice = 313;
  const proMonthlyPrice = 656;
  const starterAnnualTotal = 997;
  const teamAnnualTotal = 2856;
  const proAnnualTotal = 5868;
  const starterMonthlyEquivalent = Math.round(starterAnnualTotal / 12);
  const teamMonthlyEquivalent = Math.round(teamAnnualTotal / 12);
  const proMonthlyEquivalent = Math.round(proAnnualTotal / 12);

  const starterPricePrimary = cadence === 'annual' ? `$${starterMonthlyEquivalent}` : `$${starterMonthlyPrice}`;
  const starterPriceStrikethrough = cadence === 'annual' ? `$${starterMonthlyPrice}` : null;
  const starterPriceSecondary = cadence === 'annual' ? `per month ($${starterAnnualTotal.toLocaleString()}/p.a.)` : 'per month';
  const teamPricePrimary = cadence === 'annual' ? `$${teamMonthlyEquivalent}` : `$${teamMonthlyPrice}`;
  const teamPriceStrikethrough = cadence === 'annual' ? `$${teamMonthlyPrice}` : null;
  const teamPriceSecondary = cadence === 'annual' ? `per month ($${teamAnnualTotal.toLocaleString()}/p.a.)` : 'per month';
  const proPricePrimary = cadence === 'annual' ? `$${proMonthlyEquivalent}` : `$${proMonthlyPrice}`;
  const proPriceStrikethrough = cadence === 'annual' ? `$${proMonthlyPrice}` : null;
  const proPriceSecondary = cadence === 'annual' ? `per month ($${proAnnualTotal.toLocaleString()}/p.a.)` : 'per month';

  // Trial lifecycle. A non-enterprise org is either: pre-trial (never subscribed),
  // on a free trial, or on a paid plan. The trial can start on either cadence
  // (monthly/annual) — the card is captured up front and charged at trial end.
  const isTrialing = status === 'trialing';
  const isPaidActive = status === 'active';
  // Until /billing/status answers, `billing` is null and every derived value
  // above reads as a free, never-subscribed org — so the tab would open on a
  // trial pitch even for a paying customer. Hold the trial framing back until
  // we actually know; the plan cards still render, just without the claim.
  const billingReady = !!billing;
  const preTrial = billingReady && !isTrialing && !isPaidActive;
  const planLabel = plan === 'pro' ? 'Professional' : plan === 'team' ? 'Team' : plan === 'starter' ? 'Starter' : plan;
  // Ordering for the upgrade/downgrade CTA on each plan card.
  const planRank = { free: 0, starter: 1, team: 2, pro: 3, enterprise: 4 };

  // Resolve the CTA + click handler for a Starter/Pro plan card given trial state.
  const planCardActionFor = (tier) => {
    // Plan unknown yet: show the cards with a neutral CTA rather than guessing
    // at a trial offer or an upgrade. Still clickable — checkout is the right
    // destination either way, and the server settles what the org is entitled to.
    if (!billingReady) {
      return {
        cta: 'Choose this plan',
        onClick: () => startCheckout({ tier, cadence }),
        disabled: billingLoading,
        current: false,
      };
    }
    if (preTrial) {
      return {
        cta: 'Start 7-day free trial',
        onClick: () => startCheckout({ tier, cadence, trial: true, reminder_opt_in: reminderOptIn }),
        disabled: billingLoading,
        current: false,
      };
    }
    if (isTrialing) {
      if (plan === tier) return { cta: 'On trial', onClick: undefined, disabled: true, current: true };
      return { cta: 'Manage in Stripe', onClick: openPortal, disabled: billingLoading || !canManage, current: false };
    }
    // Paid/active — preserve the existing upgrade/downgrade-via-Stripe behaviour.
    if (plan === tier) return { cta: 'Manage in Stripe', onClick: openPortal, disabled: billingLoading || !canManage, current: true };
    return {
      cta: (planRank[tier] ?? 0) > (planRank[plan] ?? 0) ? 'Upgrade plan' : 'Downgrade plan',
      onClick: () => (canManage ? openPortal() : startCheckout({ tier, cadence })),
      disabled: billingLoading,
      current: false,
    };
  };
  const starterAction = planCardActionFor('starter');
  const teamAction = planCardActionFor('team');
  const proAction = planCardActionFor('pro');

  // Professional customers are already above Team, so the card is only noise for
  // them — drop it and let the remaining two cards share the row.
  const showTeamPlan = plan !== 'pro';
  const planColClass = showTeamPlan ? 'col-12 col-md-4' : 'col-12 col-md-6';

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
    if (!domainRegex.test(domain)) {
      setError('Invalid domain format. Please enter a valid domain (e.g., example.com)');
      return;
    }
    if (allowedDomains.includes(domain)) {
      setError('Domain already in the list');
      return;
    }
    try {
      setUpdatingDomains(true);
      setError('');
      const updatedDomains = [...allowedDomains, domain];
      const { data } = await axios.put(`${API_BASE_URL}/partner/api-key/domains`, {
        allowed_domains: updatedDomains
      });
      if (data?.success) {
        setAllowedDomains(data.data.allowed_domains || []);
        setNewDomain('');
        setSuccess('Domain added');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add domain');
    } finally {
      setUpdatingDomains(false);
    }
  };

  const handleRemoveDomain = async (domainToRemove) => {
    try {
      setUpdatingDomains(true);
      setError('');
      const updatedDomains = allowedDomains.filter(d => d !== domainToRemove);
      const { data } = await axios.put(`${API_BASE_URL}/partner/api-key/domains`, {
        allowed_domains: updatedDomains
      });
      if (data?.success) {
        setAllowedDomains(data.data.allowed_domains || []);
        setSuccess('Domain removed');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to remove domain');
    } finally {
      setUpdatingDomains(false);
    }
  };

  const initialTab = useMemo(() => {
    const path = location?.pathname || '';
    return path.startsWith('/billing') ? 'billing' : 'profile';
  }, [location?.pathname]);

  const [activeTab, setActiveTab] = useState(initialTab);

  // Prefer the live role once supplierInfo has loaded; before then fall back to
  // the cached hint so the sidebar doesn't reshuffle a few seconds into the page.
  const isAdmin = supplierInfo ? supplierInfo.role === 'admin' : readCachedRole() === 'admin';

  const isEnterprise = billing?.plan === 'enterprise' && !!billing?.enterprise_activated;
  const SETTINGS_TABS = ALL_SETTINGS_TABS.filter(tab => {
    // The tab you are looking at is always in the sidebar, whatever the role
    // hint says — content without its nav item reads as a broken page.
    if (tab.id === activeTab) return true;
    if (tab.id === 'billing') return isAdmin && !isEnterprise;
    if (tab.adminOnly) return isAdmin;
    return true;
  });

  const fetchTeamData = async () => {
    setTeamLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/team`);
      if (res.data.success) {
        const { members, invitations, seat_limit, seat_used, can_invite_members } = res.data.data;
        setTeamMembers(members);
        setTeamInvitations(invitations);
        setTeamSeatLimit(seat_limit);
        setTeamSeatUsed(seat_used);
        setCanInviteMembers(can_invite_members);
      }
    } catch (err) {
      console.error('Failed to load team data:', err);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    const ok = await confirm({
      title: 'Remove this team member?',
      message: 'They will lose access to your Zilch account immediately.',
      confirmLabel: 'Remove member',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE_URL}/auth/team/${userId}`);
      fetchTeamData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove team member');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    const ok = await confirm({
      title: 'Revoke this invitation?',
      message: 'The link in their invitation email will stop working. You can always invite them again later.',
      confirmLabel: 'Revoke invite',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE_URL}/auth/invitations/${invitationId}`);
      fetchTeamData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (activeTab === 'team' && user) {
      fetchTeamData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  return (
    <div className="sp-shell">
      <div className="sp-container">

        {error && (
          <div className="sp-alert sp-alert--error">
            <FiAlertTriangle size={14} />
            {error}
          </div>
        )}
        {success && (
          <div className="sp-alert sp-alert--success">
            <FiCheck size={14} />
            {success}
          </div>
        )}

        <div className="sp-layout">

          {/* ── Sidebar ── */}
          <nav className="sp-nav" aria-label="Settings navigation">
            <span className="sp-nav__label">Settings</span>
            {SETTINGS_TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                className={`sp-nav__item ${activeTab === id ? 'sp-nav__item--active' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </nav>

          {/* ── Content ── */}
          <div>

            {/* ══ PROFILE TAB ══════════════════════════════════════ */}
            {activeTab === 'profile' && (
              <>
                {profileLoading && (
                  <div className="sp-section" style={{ padding: '24px', color: '#94a3b8', fontSize: 13 }}>
                    Loading profile…
                  </div>
                )}
{/* Account */}
                <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">Account</div>
                      <div className="sp-section__desc">Your organisation details</div>
                    </div>
                    <div className="sp-section__actions">
                      {editingAccount ? (
                        <>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setEditingAccount(false)}
                            disabled={savingAccount}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm btn-primary ms-2"
                            onClick={handleSaveAccount}
                            disabled={savingAccount}
                          >
                            {savingAccount ? 'Saving…' : 'Save'}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleEditAccount}
                          disabled={!profile}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Organisation</div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount ? (
                        <input
                          className="form-control form-control-sm"
                          value={accountForm.name}
                          onChange={e => setAccountForm(f => ({ ...f, name: e.target.value }))}
                        />
                      ) : (
                        <span className="sp-row__value">{profile?.name || '—'}</span>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Logo</div>
                      <div className="sp-row__hint text-muted small">
                        Used as the default image for products without photos.
                      </div>
                    </div>
                    <div className="sp-row__right" style={{ gap: 10 }}>
                      {profile?.logo_url ? (
                        <>
                          <img
                            src={profile.logo_url}
                            alt="Organisation logo"
                            style={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', padding: 4 }}
                          />
                          <label
                            className="btn btn-sm btn-outline-secondary"
                            style={{ fontSize: 12, borderRadius: 6, cursor: 'pointer', marginBottom: 0 }}
                          >
                            {uploadingLogo ? 'Uploading…' : 'Replace'}
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploadingLogo} />
                          </label>
                          <button
                            type="button"
                            className="sp-icon-btn"
                            onClick={handleRemoveLogo}
                            disabled={uploadingLogo}
                            title="Remove logo"
                          >
                            <FiX size={13} />
                          </button>
                        </>
                      ) : (
                        <label
                          className="btn btn-sm btn-outline-secondary"
                          style={{ fontSize: 12, borderRadius: 6, cursor: 'pointer', marginBottom: 0 }}
                        >
                          {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploadingLogo} />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Email</div>
                    </div>
                    <div className="sp-row__right">
                      <span className="sp-row__value">{user?.email || supplierInfo?.email || '—'}</span>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Organisation ID</div>
                    </div>
                    <div className="sp-row__right">
                      {(() => {
                        const orgId = profile?.id || supplierInfo?.supplier_id;
                        return orgId ? (
                          <>
                            <span className="sp-row__value sp-row__value--mono sp-row__value--full">
                              {orgId}
                            </span>
                            <button
                              type="button"
                              className={`sp-icon-btn ${orgIdCopied ? 'sp-icon-btn--active' : ''}`}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(orgId);
                                  setOrgIdCopied(true);
                                  setTimeout(() => setOrgIdCopied(false), 1500);
                                } catch (_) {}
                              }}
                              aria-label="Copy organisation ID"
                            >
                              {orgIdCopied ? <FiCheck size={13} /> : <FiCopy size={13} />}
                            </button>
                          </>
                        ) : (
                          <span className="sp-row__value sp-row__value--mono">—</span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Phone</div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount ? (
                        <input
                          className="form-control form-control-sm"
                          value={accountForm.phone_1}
                          onChange={e => setAccountForm(f => ({ ...f, phone_1: e.target.value }))}
                        />
                      ) : (
                        <span className="sp-row__value">{profile?.phone_1 || '—'}</span>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Website</div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount ? (
                        <input
                          className="form-control form-control-sm"
                          value={accountForm.website}
                          onChange={e => setAccountForm(f => ({ ...f, website: e.target.value }))}
                        />
                      ) : (
                        <span className="sp-row__value">{profile?.website || '—'}</span>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Country</div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount ? (
                        <select
                          className="form-select form-select-sm"
                          value={accountForm.country}
                          onChange={e => setAccountForm(f => ({ ...f, country: e.target.value }))}
                        >
                          <option value="">Choose a country…</option>
                          <optgroup label="Most used">
                            {COMMON_COUNTRIES.map(code => <option key={`common-${code}`} value={code}>{countryName(code)}</option>)}
                          </optgroup>
                          <optgroup label="All countries">
                            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                          </optgroup>
                        </select>
                      ) : (
                        <span className="sp-row__value">
                          {(countryCodeOf(profile?.country) && countryName(countryCodeOf(profile?.country))) || profile?.country || '—'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Warehouse Address</div>
                      <div className="sp-row__hint text-muted small">
                        Optional. Leave blank if you don't have a warehouse.
                      </div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount ? (
                        <div style={{ minWidth: 280, flex: 1 }}>
                          <AddressAutocomplete
                            value={accountForm.warehouse_address || ''}
                            placeholder="Start typing your warehouse address…"
                            inputClassName="form-control form-control-sm"
                            onSelect={({ address, lat, lng }) => {
                              setAccountForm(f => ({
                                ...f,
                                warehouse_address: address || '',
                                warehouse_lat: lat ?? null,
                                warehouse_lng: lng ?? null,
                              }));
                            }}
                          />
                        </div>
                      ) : (
                        <span className="sp-row__value">
                          {getWarehouseFromAddresses(profile?.addresses)?.raw || '—'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Account Type</div>
                    </div>
                    <div className="sp-row__right">
                      {editingAccount && !isEnterprise ? (
                        <div className="d-flex gap-2">
                          {['Supplier', 'Distributor'].map(type => (
                            <button
                              key={type}
                              type="button"
                              className={`btn btn-sm${accountForm.account_type === type ? ' btn-dark' : ' btn-outline-secondary'}`}
                              onClick={() => setAccountForm(f => ({ ...f, account_type: type }))}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="sp-row__value">{profile?.account_type || '—'}</span>
                      )}
                    </div>
                  </div>

                  {profile?.account_type === 'Supplier' && (
                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">Public catalogue</div>
                        <div className="sp-row__hint text-muted small">
                          Let distributors browse your products and pull them into their own catalogue.
                          You'll be notified weekly when distributors add your products.
                          {profile?.is_results_public === false && (
                            <> <strong>Hidden from distributors while your results pages are private.</strong></>
                          )}
                        </div>
                      </div>
                      <div className="sp-row__right">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="catalogueVisibilityToggle"
                            checked={!!profile?.is_catalogue_public}
                            disabled={savingCatalogueVisibility}
                            onChange={(e) => handleCatalogueVisibilityToggle(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="catalogueVisibilityToggle">
                            {profile?.is_catalogue_public ? 'Public' : 'Private'}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Applies to every account type — a distributor publishes
                      results pages for the products it owns just as a supplier does. */}
                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Public results pages</div>
                      <div className="sp-row__hint text-muted small">
                        {profile?.is_results_public === false ? (
                          <>
                            Results pages are private. Only your team can open them —{' '}
                            <strong>distributors who added your products no longer see them, and
                            links you've shared, emailed reports and your website widget's link no
                            longer work</strong>. Search engines can't index them.
                          </>
                        ) : (
                          <>
                            Anyone with the link can view a product's footprint, and search engines
                            can index it. Turn this off to keep your assessments to your team only —
                            distributors who've added your products lose them too, until you turn
                            it back on.
                          </>
                        )}
                      </div>
                    </div>
                    <div className="sp-row__right">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="resultsVisibilityToggle"
                          checked={profile?.is_results_public !== false}
                          disabled={savingResultsVisibility || !profile}
                          onChange={(e) => handleResultsVisibilityToggle(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="resultsVisibilityToggle">
                          {profile?.is_results_public === false ? 'Private' : 'Public'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Role</div>
                    </div>
                    <div className="sp-row__right">
                      <span
                        className={`badge ${isAdmin ? 'text-bg-primary' : 'text-bg-secondary'}`}
                        style={{ fontSize: 12, fontWeight: 600 }}
                      >
                        {supplierInfo?.role || 'Member'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PDF report */}
                <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">PDF report</div>
                      <div className="sp-section__desc">
                        What your downloaded and emailed product reports include
                      </div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Methodology pages</div>
                      <div className="sp-row__hint text-muted small">
                        {profile?.pdf_include_methodology === false ? (
                          <>
                            Reports are the one-page impact summary only. The calculation
                            methodology, data sources are disclaimer are removed (not recommended).
                          </>
                        ) : (
                          <>
                            Two pages explaining how the footprint was calculated. Turn
                            this off to send the one-page summary on its own.
                          </>
                        )}
                      </div>
                    </div>
                    <div className="sp-row__right">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="reportMethodologyToggle"
                          checked={profile?.pdf_include_methodology !== false}
                          disabled={savingReportOption || !profile || !canCustomiseReport}
                          onChange={(e) => handleReportOptionToggle('pdf_include_methodology', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="reportMethodologyToggle">
                          {profile?.pdf_include_methodology === false ? 'Hidden' : 'Included'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Zilch branding</div>
                      <div className="sp-row__hint text-muted small">
                        {profile?.pdf_show_zilch_branding === false ? (
                          <>
                            Zilch branding is hidden from the PDF report. The Zilch logos and the "Zilch Calculation Methodology" title are removed.
                          </>
                        ) : (
                          <>
                            Zilch branding is shown on the PDF report. The Zilch logos and the "Zilch Calculation Methodology" title are included.
                          </>
                        )}
                      </div>
                    </div>
                    <div className="sp-row__right">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="reportBrandingToggle"
                          checked={profile?.pdf_show_zilch_branding !== false}
                          disabled={savingReportOption || !profile || !canCustomiseReport}
                          onChange={(e) => handleReportOptionToggle('pdf_show_zilch_branding', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="reportBrandingToggle">
                          {profile?.pdf_show_zilch_branding === false ? 'Hidden' : 'Shown'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Report date</div>
                      <div className="sp-row__hint text-muted small">
                        {profile?.pdf_show_full_date === false ? (
                          <>
                            Reports are dated by year e.g. {currentYear}
                          </>
                        ) : (
                          <>
                            Reports carry the full assessment date e.g. 01 Jul {currentYear}.
                          </>
                        )}
                      </div>
                    </div>
                    <div className="sp-row__right">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="reportFullDateToggle"
                          checked={profile?.pdf_show_full_date !== false}
                          disabled={savingReportOption || !profile || !canCustomiseReport}
                          onChange={(e) => handleReportOptionToggle('pdf_show_full_date', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="reportFullDateToggle">
                          {profile?.pdf_show_full_date === false ? 'Year only' : 'Full date'}
                        </label>
                      </div>
                    </div>
                  </div>

                  {billing && !canCustomiseReport && (
                    <div className="sp-section__footer">
                      <span className="sp-section__footer-note">
                        Customising the report is included on the Professional plan and above.
                        Upgrade to send the one-page summary on its own, remove the Zilch mark or
                        date reports by year.
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate('/billing')}
                      >
                        See plans
                      </button>
                    </div>
                  )}
                </div>

                {/* Usage */}
                {isAdmin && (
                  <div className="sp-section">
                    <div className="sp-section__head">
                      <div className="sp-section__head-text">
                        <div className="sp-section__title">Usage</div>
                        <div className="sp-section__desc">Your current plan and usage</div>
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ms-2"
                          onClick={openPortal}
                          disabled={billingLoading}
                        >
                          Manage in Stripe
                        </button>
                      )}
                    </div>

                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">Status</div>
                      </div>
                      <div className="sp-row__right">
                        <span className={`badge rounded-pill ${currentPlanDetails.statusClass}`}>
                          {currentPlanDetails.statusLabel}
                        </span>
                      </div>
                    </div>

                    {currentPlanDetails.periodEnd && (
                      <div className="sp-row">
                        <div className="sp-row__left">
                          <div className="sp-row__label">Renews</div>
                        </div>
                        <div className="sp-row__right">
                          <span className="sp-row__value">{currentPlanDetails.periodEnd}</span>
                        </div>
                      </div>
                    )}

                    {currentPlanDetails.assessmentUsed != null && (isEnterprise || isOverride || currentPlanDetails.assessmentLimit === 0 ? (
                      <div className="sp-row">
                        <div className="sp-row__left">
                          <div className="sp-row__label">Assessments</div>
                        </div>
                        <div className="sp-row__right">
                          <span className="sp-row__value">{currentPlanDetails.assessmentUsed} / Unlimited</span>
                        </div>
                      </div>
                    ) : currentPlanDetails.assessmentLimit != null && currentPlanDetails.assessmentLimit > 0 && (
                      <div className="sp-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="sp-row__label">Assessments</div>
                          <span className="sp-row__value">{currentPlanDetails.assessmentUsed} / {currentPlanDetails.assessmentLimit}</span>
                        </div>
                        <div className="progress billing-progress" role="progressbar" aria-valuenow={currentPlanDetails.assessmentPercent} aria-valuemin="0" aria-valuemax="100">
                          <div className="progress-bar" style={{ width: `${currentPlanDetails.assessmentPercent}%` }} />
                        </div>
                      </div>
                    ))}

                    {currentPlanDetails.domainLimit != null && (
                      <div className="sp-row">
                        <div className="sp-row__left">
                          <div className="sp-row__label">Widget domains</div>
                        </div>
                        <div className="sp-row__right">
                          <span className="sp-row__value">{currentPlanDetails.domainLimit}</span>
                        </div>
                      </div>
                    )}

                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">PDF downloads</div>
                      </div>
                      <div className="sp-row__right">
                        <span className={`badge rounded-pill ${currentPlanDetails.canDownloadPdf ? 'text-bg-success' : 'text-bg-light'}`}>
                          {currentPlanDetails.canDownloadPdf ? 'Included' : 'Not included'}
                        </span>
                      </div>
                    </div>

                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">Methodology</div>
                      </div>
                      <div className="sp-row__right">
                        <span className={`badge rounded-pill ${currentPlanDetails.canViewMethodology ? 'text-bg-success' : 'text-bg-light'}`}>
                          {currentPlanDetails.canViewMethodology ? 'Included' : 'Not included'}
                        </span>
                      </div>
                    </div>

                    {!isEnterprise && (
                    <div className="sp-section__footer">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setActiveTab('billing')}
                      >
                        View plans
                      </button>
                    </div>
                    )}
                  </div>
                )}

                {/* Security */}
                <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">Security</div>
                      <div className="sp-section__desc">Manage your password</div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Password</div>
                      <div className="sp-row__sub">Keep your account secure with a strong password</div>
                    </div>
                    <div className="sp-row__right">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setShowChangePasswordModal(true)}
                      >
                        <FiLock size={12} className="me-1" />
                        Change password
                      </button>
                    </div>
                  </div>
                </div>

                {/* API Access */}
                <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">API Access</div>
                      <div className="sp-section__desc">Embed the Zilch widget using your api key</div>
                    </div>
                  </div>

                  {!hasWidgetAccess ? (
                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">Upgrade to Starter</div>
                        <div className="sp-row__sub">
                          Widget embedding is included on every paid plan. The Partner API is
                          included on the Team plan and above.
                          {!!billing?.starter_mcp_promo && ' Starter also lets you connect AI assistants to Zilch (MCP), for a limited time.'}
                        </div>
                      </div>
                      <div className="sp-row__right">
                        {isAdmin ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => setActiveTab('billing')}
                          >
                            Upgrade plan
                          </button>
                        ) : (
                          <span className="text-muted" style={{ fontSize: 13 }}>Ask an admin to upgrade</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="sp-row">
                        <div className="sp-row__left">
                          <div className="sp-row__label">API Key</div>
                          {apiKeyRow?.last_used_at && (
                            <div className="sp-row__sub">
                              Last used {new Date(apiKeyRow.last_used_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <div className="sp-row__right">
                          {apiKeyRow?.api_key ? (
                            <>
                              {/* Publishable key — safe to embed in web pages, so show it
                                  in plain text (unlike the secret key below). */}
                              <span className="sp-row__value sp-row__value--mono">
                                {apiKeyRow.api_key}
                              </span>
                              <button
                                type="button"
                                className={`sp-icon-btn ${copied ? 'sp-icon-btn--active' : ''}`}
                                onClick={async () => {
                                  if (!apiKeyRow?.api_key) return;
                                  try {
                                    await navigator.clipboard.writeText(apiKeyRow.api_key);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 1500);
                                  } catch (_) {}
                                }}
                                aria-label="Copy API key"
                              >
                                {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
                              </button>
                            </>
                          ) : (
                            <span className="sp-row__value sp-row__value--muted">Not generated</span>
                          )}
                        </div>
                      </div>

                      <div className="sp-section__footer">
                        {apiKeyRow?.api_key ? (
                          <>
                            <span className="sp-section__footer-note">
                              Rotating invalidates your current key immediately.
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => setShowConfirmRotate(true)}
                              disabled={generating}
                            >
                              <FiRefreshCw size={12} className="me-1" />
                              Rotate key
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="sp-section__footer-note">
                              Generate a key to start using the Zilch widget.
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleGenerateOrRotate}
                              disabled={generating}
                            >
                              {generating ? 'Generating…' : 'Generate key'}
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Secret API Key — Partner API, Zapier and the MCP (never in a browser page) */}
                {hasWidgetAccess && (
                  <div className="sp-section">
                    <div className="sp-section__head">
                      <div className="sp-section__head-text">
                        <div className="sp-section__title">Secret API Key</div>
                        <div className="sp-section__desc">
                          For the{' '}
                          <a href="https://tryzilch.com/help/partner-api" target="_blank" rel="noopener noreferrer">
                            Partner API
                          </a>
                          , the{' '}
                          <a href="https://tryzilch.com/help/zapier" target="_blank" rel="noopener noreferrer">
                            Zapier integration
                          </a>{' '}
                          and{' '}
                          <a href="https://tryzilch.com/help/connect-ai" target="_blank" rel="noopener noreferrer">
                            connecting AI assistants (MCP)
                          </a>
                          . Use it from your own server or an assistant's connector settings.
                          Never in a web page or the widget.
                        </div>
                      </div>
                      <FiLock size={16} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }} />
                    </div>

                    <div className="sp-row">
                      <div className="sp-row__left">
                        <div className="sp-row__label">Secret key</div>
                        {secretKeyRow?.last_used_at && (
                          <div className="sp-row__sub">
                            Last used {new Date(secretKeyRow.last_used_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="sp-row__right">
                        {secretKeyRow?.api_key ? (
                          <>
                            <span className="sp-row__value sp-row__value--mono">
                              {secretKeyVisible
                                ? secretKeyRow.api_key
                                : secretKeyRow.api_key.replace(/./g, '•')}
                            </span>
                            <button
                              type="button"
                              className="sp-icon-btn"
                              onClick={() => setSecretKeyVisible(v => !v)}
                              aria-label={secretKeyVisible ? 'Hide secret key' : 'Show secret key'}
                            >
                              {secretKeyVisible ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                            </button>
                            <button
                              type="button"
                              className={`sp-icon-btn ${secretCopied ? 'sp-icon-btn--active' : ''}`}
                              onClick={async () => {
                                if (!secretKeyRow?.api_key) return;
                                try {
                                  await navigator.clipboard.writeText(secretKeyRow.api_key);
                                  setSecretCopied(true);
                                  setTimeout(() => setSecretCopied(false), 1500);
                                } catch (_) {}
                              }}
                              aria-label="Copy secret key"
                            >
                              {secretCopied ? <FiCheck size={13} /> : <FiCopy size={13} />}
                            </button>
                          </>
                        ) : (
                          <span className="sp-row__value sp-row__value--muted">Not generated</span>
                        )}
                      </div>
                    </div>

                    {mcpLimitedTime && (
                      <div className="sp-row">
                        <div className="sp-row__left">
                          <div className="sp-row__label">AI assistants (MCP)</div>
                          <div className="sp-row__sub">
                            Included on Starter for a limited time. Connect ChatGPT, Copilot or
                            Claude Code to <span className="sp-row__value--mono">{MCP_URL}</span> with
                            this key. The Partner API and Zapier need Team or above.
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="sp-section__footer">
                      {secretKeyRow?.api_key ? (
                        <>
                          <span className="sp-section__footer-note">
                            Rotating invalidates the current secret key immediately and will
                            disconnect Zapier and any AI assistant until you reconnect them. Your
                            widget is unaffected.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => setShowConfirmRotateSecret(true)}
                            disabled={generatingSecret}
                          >
                            <FiRefreshCw size={12} className="me-1" />
                            Rotate secret key
                          </button>
                        </>
                      ) : !canGenerateSecretKey ? (
                        <>
                          <span className="sp-section__footer-note">
                            The Partner API is included on the Team plan and above. Upgrade to
                            generate a secret key and connect Zapier or an AI assistant.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate('/billing')}
                          >
                            See plans
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="sp-section__footer-note">
                            {canUsePartnerApi
                              ? 'Generate a secret key to use the Partner API, connect Zapier or connect an AI assistant.'
                              : 'Generate a secret key to connect an AI assistant to Zilch.'}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleGenerateOrRotateSecret}
                            disabled={generatingSecret}
                          >
                            {generatingSecret ? 'Generating…' : 'Generate secret key'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Widget Domains */}
                {hasWidgetAccess && apiKeyRow?.api_key && (
                  <div className="sp-section">
                    <div className="sp-section__head">
                      <div className="sp-section__head-text">
                        <div className="sp-section__title">Widget Domains</div>
                        <div className="sp-section__desc">
                          Restrict which sites can embed your widget. Leave empty to allow all domains.
                        </div>
                      </div>
                      <FiGlobe size={16} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }} />
                    </div>

                    {allowedDomains.length > 0 ? (
                      <div className="sp-domain-tags">
                        {allowedDomains.map((domain, idx) => (
                          <span key={idx} className="sp-domain-tag">
                            {domain}
                            <button
                              type="button"
                              className="sp-domain-remove"
                              onClick={() => handleRemoveDomain(domain)}
                              disabled={updatingDomains}
                              aria-label={`Remove ${domain}`}
                            >
                              <FiX size={11} />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="sp-domain-empty">
                        No domains added — widget works on any site
                      </div>
                    )}

                    <div className="sp-domain-add">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="example.com"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); }
                        }}
                        disabled={updatingDomains}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleAddDomain}
                        disabled={updatingDomains || !newDomain.trim()}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <FiPlus size={13} className="me-1" />
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Integrations */}
                {/* <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">Integrations</div>
                      <div className="sp-section__desc">Connect Zilch with your other tools</div>
                    </div>
                    <FiGrid size={16} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }} />
                  </div>

                  <div className="sp-integrations">
                    <button
                      className="sp-integration-btn"
                      onClick={() => {
                        setSelectedIntegrationCategory('Accounting');
                        setShowIntegrationsModal(true);
                      }}
                    >
                      Accounting
                    </button>
                    <button className="sp-integration-btn" disabled>
                      ERP
                      <span className="sp-integration-soon">· Coming soon</span>
                    </button>
                    <button className="sp-integration-btn" disabled>
                      PIM
                      <span className="sp-integration-soon">· Coming soon</span>
                    </button>
                  </div>
                </div> */}

                {/* Session / Logout */}
                <div className="sp-section sp-section--danger">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">Session</div>
                      <div className="sp-section__desc">Sign out of your Zilch account</div>
                    </div>
                  </div>

                  <div className="sp-row">
                    <div className="sp-row__left">
                      <div className="sp-row__label">Sign out</div>
                      <div className="sp-row__sub">You will be redirected to the login page</div>
                    </div>
                    <div className="sp-row__right">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleLogout}
                      >
                        Log out
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ══ TEAM TAB ══════════════════════════════════════ */}
            {activeTab === 'team' && (
              <div>
                <div className="sp-section">
                  <div className="sp-section__head">
                    <div className="sp-section__head-text">
                      <div className="sp-section__title">Team Members</div>
                      <div className="sp-section__desc">
                        {teamSeatLimit > 0
                          ? `${teamSeatUsed} of ${teamSeatLimit} seat${teamSeatLimit !== 1 ? 's' : ''} used`
                          : `${teamSeatUsed} seat${teamSeatUsed !== 1 ? 's' : ''} used (unlimited)`}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="sp-section__actions">
                        {!canInviteMembers ? (
                          <span className="text-muted" style={{ fontSize: 13 }}>
                            Upgrade to invite members
                          </span>
                        ) : (teamSeatLimit > 0 && teamSeatUsed >= teamSeatLimit) ? (
                          <span className="text-muted" style={{ fontSize: 13 }}>
                            Seat limit reached
                          </span>
                        ) : (
                          <button
                            className="btn btn-sm btn-dark"
                            onClick={() => setShowInviteModal(true)}
                          >
                            <FiPlus size={13} className="me-1" />
                            Invite member
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {teamLoading ? (
                    <div style={{ padding: '16px 24px', color: '#94a3b8', fontSize: 13 }}>Loading team…</div>
                  ) : (
                    <div>
                      {teamMembers.map((member) => (
                        <div key={member.id} className="sp-row">
                          <div className="sp-row__left">
                            <div className="sp-row__label">{member.email}</div>
                            <div className="sp-row__sub">
                              Joined {new Date(member.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                          </div>
                          <div className="sp-row__right">
                            <span
                              className={`badge ${member.role === 'admin' ? 'text-bg-primary' : 'text-bg-secondary'}`}
                              style={{ fontSize: 11, fontWeight: 600 }}
                            >
                              {member.role}
                            </span>
                            {isAdmin && member.role !== 'admin' && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveMember(member.id)}
                                title="Remove member"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isAdmin && teamInvitations.length > 0 && (
                  <div className="sp-section">
                    <div className="sp-section__head">
                      <div className="sp-section__head-text">
                        <div className="sp-section__title">Pending Invitations</div>
                        <div className="sp-section__desc">These people have been invited but haven't signed up yet</div>
                      </div>
                    </div>
                    <div>
                      {teamInvitations.map((invite) => (
                        <div key={invite.id} className="sp-row">
                          <div className="sp-row__left">
                            <div className="sp-row__label">{invite.invitee_name}</div>
                            <div className="sp-row__sub">{invite.invitee_email}</div>
                            <div className="sp-row__sub">
                              Expires {new Date(invite.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                          </div>
                          <div className="sp-row__right">
                            <span className="badge text-bg-warning" style={{ fontSize: 11, fontWeight: 600 }}>Pending</span>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleRevokeInvitation(invite.id)}
                              title="Revoke invitation"
                            >
                              <FiX size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══ BILLING TAB ══════════════════════════════════════ */}
            {activeTab === 'billing' && (
              <div>
                {banner && (
                  <div className={`alert alert-${banner.variant} mb-4`} role="alert">
                    <div className="fw-semibold">{banner.title}</div>
                    <div style={{ fontSize: 14 }}>{banner.body}</div>
                  </div>
                )}

                {billingError && (
                  <div className="alert alert-danger mb-4" role="alert">
                    {billingError}
                  </div>
                )}

                {isOverride ? (
                  <div className="alert alert-success mb-4" role="alert">
                    This account has full access regardless of subscription.
                  </div>
                ) : isEnterprise ? (
                  <div className="alert alert-success mb-4" role="alert">
                    <strong>Enterprise Plan</strong> — Your organisation has full enterprise access with unlimited SKUs and seats.
                  </div>
                ) : (plan === 'enterprise' && !billing?.enterprise_activated) ? (
                  <div className="alert alert-warning mb-4" role="alert">
                    <strong>Enterprise Plan — Pending Activation</strong>
                    <div style={{ fontSize: 14 }}>Your enterprise account is being set up. Contact <a href="mailto:info@tryzilch.com">info@tryzilch.com</a> if you need assistance.</div>
                  </div>
                ) : (
                  <div>
                      {isTrialing && (
                        <div className="alert alert-info mb-4" role="alert">
                          <div className="fw-semibold mb-1">You're on a free trial of {planLabel}</div>
                          <div style={{ fontSize: 14 }}>
                            {currentPlanDetails.periodEnd ? (
                              <>Your trial ends on <strong>{currentPlanDetails.periodEnd}</strong>. Your card will be charged for {planLabel} then — no action needed to continue.</>
                            ) : (
                              <>Your card will be charged for {planLabel} when the trial ends — no action needed to continue.</>
                            )}
                          </div>
                          <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
                            <button type="button" className="btn btn-dark btn-sm" onClick={endTrial} disabled={billingLoading}>
                              {billingLoading ? 'Working…' : 'End trial & subscribe now'}
                            </button>
                            <label className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={!!billing?.trial_reminder_opt_in}
                                onChange={(e) => toggleTrialReminder(e.target.checked)}
                                disabled={billingLoading}
                              />
                              Email me before my trial ends
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <div>
                          <h4 className="mb-1">{preTrial ? 'Start your 7-day free trial' : 'Choose your plan'}</h4>
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            {!billingReady
                              ? 'Checking your current plan… Prices in $AUD (ex GST).'
                              : preTrial
                                ? "Pick a plan to trial — you won't be charged until it ends. Prices in $AUD (ex GST)."
                                : 'Prices in $AUD (ex GST)'}
                          </div>
                        </div>

                        <div className="billing-toggle">
                          <div className="billing-toggle__copy text-muted" style={{ fontSize: 13 }}>
                            <span className="fw-semibold">Annual</span> (
                            <span className="billing-toggle__save-text">Save up to 25%</span>)
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-label="Billing cadence"
                            aria-checked={cadence === 'annual'}
                            className={`billing-toggle__switch ${cadence === 'annual' ? 'is-active' : ''}`}
                            onClick={() => setCadence(cadence === 'annual' ? 'monthly' : 'annual')}
                            disabled={billingLoading}
                          >
                            <span className="billing-toggle__knob" />
                            <span className="visually-hidden">
                              {cadence === 'annual' ? 'Switch to monthly' : 'Switch to annual'}
                            </span>
                          </button>
                        </div>
                      </div>

                      {preTrial && (
                        <label className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: 14 }}>
                          <input
                            type="checkbox"
                            checked={reminderOptIn}
                            onChange={(e) => setReminderOptIn(e.target.checked)}
                            disabled={billingLoading}
                          />
                          Email me a reminder before my trial ends and I'm billed
                        </label>
                      )}

                      <div className="row g-3">
                        <div className={planColClass}>
                          <PlanCard
                            title="Starter"
                            subtitle="Launch a sales-ready eco-catalogue"
                            pricePrimary={starterPricePrimary}
                            priceStrikethrough={starterPriceStrikethrough}
                            priceSecondary={preTrial ? `${starterPriceSecondary} · free for 7 days` : starterPriceSecondary}
                            bullets={[
                              '100 custom product assessments',
                              'One-click product assessments',
                              'Bulk product upload (CSV)',
                              'Send custom reports to customers',
                              'Widget on one site',
                              'Product footprint reports & methodology',
                              'Public credentials page',
                              ...(billing?.starter_mcp_promo ? [billing.starter_mcp_bullet] : []),
                              'Email & chat support',
                              { text: 'Team seats', included: false },
                              { text: 'Partner API', included: false },
                            ]}
                            cta={starterAction.cta}
                            onClick={starterAction.onClick}
                            disabled={starterAction.disabled}
                            current={starterAction.current}
                            footnote="Great for selling sustainable SKUs"
                          />
                        </div>

                        {showTeamPlan && (
                        <div className={planColClass}>
                          <PlanCard
                            title="Team"
                            subtitle="For teams selling on sustainability"
                            pricePrimary={teamPricePrimary}
                            priceStrikethrough={teamPriceStrikethrough}
                            priceSecondary={preTrial ? `${teamPriceSecondary} · free for 7 days` : teamPriceSecondary}
                            bullets={[
                              'Everything in Starter',
                              '500 custom product assessments',
                              'Custom widget on 2 sites',
                              'AI Agent mode',
                              'Customised & co-branded product footprint reports',
                              'Personal account manager',
                              '2 team seats',
                              'Partner API',
                            ]}
                            cta={teamAction.cta}
                            onClick={teamAction.onClick}
                            disabled={teamAction.disabled}
                            current={teamAction.current}
                            featured={true}
                            footnote="Great for a growing sustainability team"
                          />
                        </div>
                        )}

                        <div className={planColClass}>
                          <PlanCard
                            title="Professional"
                            subtitle="For scale and transparency"
                            pricePrimary={proPricePrimary}
                            priceStrikethrough={proPriceStrikethrough}
                            priceSecondary={preTrial ? `${proPriceSecondary} · free for 7 days` : proPriceSecondary}
                            bullets={[
                              'Everything in Team',
                              '1500 custom product assessments',
                              'Custom widget on 3 sites',
                              'Personal account manager',
                              'Manufacturer portal',
                              '3 team seats',
                              'Partner API',
                            ]}
                            cta={proAction.cta}
                            onClick={proAction.onClick}
                            disabled={proAction.disabled}
                            current={proAction.current}
                            footnote="Great for a catalogue-wide rollout"
                          />
                        </div>
                      </div>

                      <div className="row g-3 mt-2">
                        <div className="col-12 col-md-6">
                          <PlanCard
                            title="Enterprise & Custom"
                            subtitle="For large teams with bespoke needs"
                            
                            bullets={[
                              'Support up to 100,000 SKUs',
                              'Multi brand organisations',
                              'Multiple sites',
                              'Custom integrations',
                            ]}
                            cta="Contact Zilch"
                            onClick={() => window.open('https://tryzilch.com/contact')}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <PlanCard
                            title="Services"
                            subtitle="Hands-on sustainability support"

                            bullets={[
                              'Product assessments',
                              'Circular economy initiatives',
                              'Offsetting support',
                              'Company-level emissions',
                            ]}
                            cta="Contact Zilch"
                            onClick={() => window.open('https://tryzilch.com/contact')}
                          />
                        </div>
                      </div>

                      <div className="row g-3 mt-2">
                        <div className="col-12">
                          <div className="card h-100">
                            <div className="card-body p-4">
                              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div>
                              <div className="fw-semibold">Need help choosing?</div>
                              <div className="text-muted" style={{ fontSize: 13 }}>
                                Tell us your SKU count and we'll recommend the right plan.
                              </div>
                            </div>
                            <a className="btn btn-outline-secondary" href="mailto:info@tryzilch.com?subject=Billing%20Help">
                              Contact Zilch
                            </a>
                          </div>

                          <div className="accordion mt-3" id="billingFaq">
                            <div className="accordion-item">
                              <h2 className="accordion-header" id="faqOne">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                                  When do changes take effect?
                                </button>
                              </h2>
                              <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="faqOne" data-bs-parent="#billingFaq">
                                <div className="accordion-body" style={{ fontSize: 14 }}>
                                  Upgrades start immediately after checkout. If you don't see changes right away, click Refresh.
                                </div>
                              </div>
                            </div>
                            <div className="accordion-item">
                              <h2 className="accordion-header" id="faqTwo">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                                  Can I cancel or downgrade?
                                </button>
                              </h2>
                              <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="faqTwo" data-bs-parent="#billingFaq">
                                <div className="accordion-body" style={{ fontSize: 14 }}>
                                  Yes. Use the <strong>Cancel subscription</strong> link at the bottom of this page to end your subscription. Downgrades and payment updates are handled via the "Manage in Stripe" button on each plan card.
                                </div>
                              </div>
                            </div>
                            <div className="accordion-item">
                              <h2 className="accordion-header" id="faqThree">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                                  What happens if I hit my limits?
                                </button>
                              </h2>
                              <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="faqThree" data-bs-parent="#billingFaq">
                                <div className="accordion-body" style={{ fontSize: 14 }}>
                                  Upgrade your plan or contact Zilch for more capacity.
                                </div>
                              </div>
                            </div>
                          </div>

                              <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                                Payments processed securely by <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">Stripe</a>.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {(plan === 'starter' || plan === 'team' || plan === 'pro') && status !== 'canceled' && (
                        <div className="text-center mt-4 mb-2">
                          <button
                            type="button"
                            className="btn btn-link text-muted text-decoration-underline"
                            style={{ fontSize: 13 }}
                            onClick={() => setShowCancelModal(true)}
                          >
                            Cancel subscription
                          </button>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <InviteUserModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          fetchTeamData();
          setSuccess('Invitation sent!');
        }}
      />

      <ChangePasswordModal
        show={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={handleChangePassword}
      />

      <CancelSubscriptionModal
        show={showCancelModal}
        plan={plan}
        retentionOfferUsed={!!billing?.retention_offer_used}
        onClose={() => setShowCancelModal(false)}
        onStayed={() => refreshBillingInfo && refreshBillingInfo()}
        onCancelled={() => refreshBillingInfo && refreshBillingInfo()}
      />

      {/* <IntegrationsModal
        show={showIntegrationsModal}
        onClose={() => setShowIntegrationsModal(false)}
        initialCategory={selectedIntegrationCategory}
      /> */}

      {showConfirmRotate && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-white">
              <div className="modal-header border-0">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <FiAlertTriangle size={20} className="text-danger" />
                  Rotate API Key
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowConfirmRotate(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <p className="mb-2">Are you sure you want to rotate your API key?</p>
                <p className="mb-0" style={{ fontSize: 14 }}>
                  This will immediately invalidate your current key. Any applications using the old key will stop working until you update them.
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmRotate(false)}
                  disabled={generating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleGenerateOrRotate}
                  disabled={generating}
                >
                  {generating ? 'Rotating…' : 'Rotate key'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmRotateSecret && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-white">
              <div className="modal-header border-0">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <FiAlertTriangle size={20} className="text-danger" />
                  Rotate Secret API Key
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowConfirmRotateSecret(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <p className="mb-2">Are you sure you want to rotate your secret API key?</p>
                <p className="mb-0" style={{ fontSize: 14 }}>
                  This immediately invalidates the current secret key. Any Zap or server-side
                  integration using it stops working until you paste in the new one. Your widget
                  uses a separate publishable key and will keep working.
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmRotateSecret(false)}
                  disabled={generatingSecret}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleGenerateOrRotateSecret}
                  disabled={generatingSecret}
                >
                  {generatingSecret ? 'Rotating…' : 'Rotate secret key'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
